// ─────────────────────────────────────────────────────────────────────
// AnnouncementsBoard — Round 9 (2026), revised UI.
//
// Card-grid layout: each announcement renders as a self-contained card
// with a full-width banner image on top, title + meta + body below,
// edit/delete pinned to the corner, and a hover-lift effect. A "NEW"
// badge highlights posts from the last 48 hours.
//
// Permissions are taken from /api/announcements/can-manage:
//   - HR  OR  IT Admin  OR  SuperUser  →  CAN see the "+ New" button,
//                                          edit & delete every item
//   - Everyone else                     →  read-only
//
// The component renders inline images directly from base64 strings
// returned by the API — no second HTTP round-trip needed.
// ─────────────────────────────────────────────────────────────────────
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
    Card, CardBody, CardHeader, Button, Spinner, Badge,
    Modal, ModalHeader, ModalBody, ModalFooter,
    Form, FormGroup, Label, Input,
} from "reactstrap";
import {
    fetchAnnouncements,
    canManageAnnouncements,
    createAnnouncement,
    updateAnnouncement,
    deleteAnnouncement,
    fileToImagePayload,
    announcementImageSrc,
} from "../../api/announcements";

// 5 MB — matches AnnouncementService.MaxImageBytes on the server.
const MAX_IMAGE_BYTES = 5 * 1024 * 1024;

// Posts within this many hours get a "NEW" badge.
const NEW_THRESHOLD_HOURS = 48;

// Banner placeholder gradient palette — picked deterministically per
// announcement id so the same row always renders the same color.
const GRADIENTS = [
    "linear-gradient(135deg, #5b50d6 0%, #d65fa6 100%)",
    "linear-gradient(135deg, #5fb3d6 0%, #5b50d6 100%)",
    "linear-gradient(135deg, #f7b84b 0%, #d65fa6 100%)",
    "linear-gradient(135deg, #2fa97e 0%, #2bb3c6 100%)",
    "linear-gradient(135deg, #d65f5f 0%, #f7b84b 100%)",
    "linear-gradient(135deg, #6f5fe3 0%, #2bb3c6 100%)",
];
const gradientFor = (id) => GRADIENTS[Math.abs(Number(id) || 0) % GRADIENTS.length];

// "2h ago" / "Yesterday" / "Mar 15" / "Mar 15, 2024"
const formatRelativeTime = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return String(iso);
    const now = new Date();
    const diffMs = now - d;
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1)    return "Just now";
    if (diffMin < 60)   return `${diffMin}m ago`;
    const diffH = Math.floor(diffMin / 60);
    if (diffH < 24)     return `${diffH}h ago`;
    const diffD = Math.floor(diffH / 24);
    if (diffD === 1)    return "Yesterday";
    if (diffD < 7)      return `${diffD}d ago`;
    const sameYear = d.getFullYear() === now.getFullYear();
    return d.toLocaleDateString(undefined,
        sameYear ? { month: "short", day: "numeric" }
                 : { month: "short", day: "numeric", year: "numeric" });
};

const isRecent = (iso, hours = NEW_THRESHOLD_HOURS) => {
    if (!iso) return false;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return false;
    return (Date.now() - d.getTime()) < hours * 60 * 60 * 1000;
};

const computeInitials = (name) => {
    if (!name) return "?";
    const parts = String(name).trim().split(/\s+/);
    const first = parts[0]?.[0] || "";
    const last  = parts.length > 1 ? parts[parts.length - 1][0] : "";
    return (first + last).toUpperCase() || "?";
};

// ─────────────────────────────────────────────────────────────────────
// Board (wrapper)
// ─────────────────────────────────────────────────────────────────────
export default function AnnouncementsBoard() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);
    const [canManage, setCanManage] = useState(false);

    const [editorOpen, setEditorOpen] = useState(false);
    const [editing, setEditing] = useState(null);    // null = creating

    // Carousel state — Round 10b.
    //   currentIndex   which item is currently shown
    //   hovered        pauses auto-rotate when the user is mousing over
    const [currentIndex, setCurrentIndex] = useState(0);
    const [hovered, setHovered] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        setLoadError(null);
        try {
            const [list, perm] = await Promise.all([
                fetchAnnouncements(),
                canManageAnnouncements(),
            ]);
            setItems(list);
            setCanManage(perm);
        } catch {
            setLoadError("Could not load announcements.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    // Keep currentIndex valid when the list shrinks (e.g. after delete).
    useEffect(() => {
        if (items.length === 0) { setCurrentIndex(0); return; }
        if (currentIndex >= items.length) setCurrentIndex(items.length - 1);
    }, [items.length, currentIndex]);

    // Auto-rotate every 6 seconds. Pauses when:
    //   - the user is hovering the card (so they can finish reading),
    //   - the editor modal is open (so the carousel doesn't move under them),
    //   - there is 0 or 1 announcement (nothing to rotate).
    useEffect(() => {
        if (hovered || editorOpen || items.length <= 1) return;
        const t = setInterval(() => {
            setCurrentIndex((i) => (i + 1) % items.length);
        }, 6000);
        return () => clearInterval(t);
    }, [hovered, editorOpen, items.length]);

    const goPrev = () => setCurrentIndex((i) => (i - 1 + items.length) % items.length);
    const goNext = () => setCurrentIndex((i) => (i + 1) % items.length);

    const openCreate = () => { setEditing(null); setEditorOpen(true); };
    const openEdit   = (a) => { setEditing(a);    setEditorOpen(true); };

    const onSaved = (saved) => {
        setEditorOpen(false);
        setEditing(null);
        setItems((prev) => {
            const exists = prev.some((x) => x.id === saved.id);
            return exists ? prev.map((x) => (x.id === saved.id ? saved : x))
                          : [saved, ...prev];
        });
    };

    const onDelete = async (a) => {
        if (!window.confirm(`Delete the announcement "${a.title}"?`)) return;
        try {
            await deleteAnnouncement(a.id);
            setItems((prev) => prev.filter((x) => x.id !== a.id));
        } catch {
            window.alert("Could not delete. Please retry.");
        }
    };

    return (
        <Card className="shadow-sm border-0 mb-3">
            <CardHeader className="bg-white border-0 pt-3 px-3 pb-2 d-flex align-items-center gap-2">
                <span
                    className="d-inline-flex align-items-center justify-content-center"
                    style={{ width: 30, height: 30, borderRadius: 8, background: "var(--ann-chip-bg, #eef1fa)", color: "var(--ann-chip-fg, #5b50d6)", flexShrink: 0 }}
                    aria-hidden="true"
                >
                    <i className="ri-megaphone-line" style={{ fontSize: 16 }} />
                </span>
                <h6 className="mb-0 fw-semibold flex-grow-1" style={{ color: "var(--ssw-text-1, #212529)" }}>
                    Announcements
                </h6>
                {canManage && (
                    <Button color="primary" size="sm" onClick={openCreate}>
                        <i className="ri-add-line align-bottom me-1"></i>New Announcement
                    </Button>
                )}
            </CardHeader>
            {/* Round PREF-1a — inline colors converted to theme tokens (owner:
                announcement background stayed white in dark themes). The var()
                fallbacks are the exact previous values, so LIGHT mode is
                pixel-identical; dark themes define the --ann-* tokens in
                appDarkThemes.css. */}
            <CardBody className="p-3" style={{ background: "var(--ann-well-bg, #f7f8fb)" }}>
                {loading ? (
                    <div className="text-center text-muted py-5">
                        <Spinner size="sm" /> Loading…
                    </div>
                ) : loadError ? (
                    <div className="text-center text-danger py-5 small">{loadError}</div>
                ) : items.length === 0 ? (
                    <div className="text-center text-muted py-5 small">
                        <i className="ri-megaphone-line d-block mb-2" style={{ fontSize: 32, opacity: 0.4 }}></i>
                        No announcements yet
                        {canManage && (
                            <div className="mt-2">
                                <Button color="link" size="sm" onClick={openCreate}>
                                    <i className="ri-add-line align-bottom me-1"></i>Post the first one
                                </Button>
                            </div>
                        )}
                    </div>
                ) : (
                    <div
                        onMouseEnter={() => setHovered(true)}
                        onMouseLeave={() => setHovered(false)}
                    >
                        {/* ─── Carousel viewport ───────────────────── */}
                        <div
                            className="position-relative"
                            style={{ overflow: "hidden", borderRadius: 12 }}
                        >
                            <div
                                style={{
                                    display: "flex",
                                    transform: `translateX(-${currentIndex * 100}%)`,
                                    transition: "transform 0.4s ease",
                                }}
                            >
                                {items.map((a) => (
                                    <div key={a.id} style={{ flex: "0 0 100%", minWidth: 0, padding: "0 2px" }}>
                                        <AnnouncementCard
                                            a={a}
                                            canManage={canManage}
                                            onEdit={() => openEdit(a)}
                                            onDelete={() => onDelete(a)}
                                        />
                                    </div>
                                ))}
                            </div>

                            {/* Prev / Next chevrons — only when there's more than one */}
                            {items.length > 1 && (
                                <>
                                    <CarouselChevron direction="prev" onClick={goPrev} />
                                    <CarouselChevron direction="next" onClick={goNext} />
                                </>
                            )}
                        </div>

                        {/* Dots indicator + counter */}
                        {items.length > 1 && (
                            <div className="d-flex align-items-center justify-content-center gap-2 mt-3">
                                {items.map((a, i) => (
                                    <button
                                        key={a.id}
                                        type="button"
                                        onClick={() => setCurrentIndex(i)}
                                        aria-label={`Go to announcement ${i + 1}`}
                                        style={{
                                            width:  i === currentIndex ? 24 : 8,
                                            height: 8,
                                            borderRadius: 999,
                                            background: i === currentIndex ? "var(--ann-dot-active, #5b50d6)" : "var(--ann-dot, #cdd2de)",
                                            border: "none",
                                            padding: 0,
                                            cursor: "pointer",
                                            transition: "width 0.25s ease, background 0.2s ease",
                                        }}
                                    />
                                ))}
                                <span className="ms-2 text-muted small">
                                    {currentIndex + 1} / {items.length}
                                </span>
                            </div>
                        )}
                    </div>
                )}
            </CardBody>

            <AnnouncementEditor
                isOpen={editorOpen}
                onClose={() => { setEditorOpen(false); setEditing(null); }}
                onSaved={onSaved}
                initial={editing}
            />
        </Card>
    );
}

// ─────────────────────────────────────────────────────────────────────
// Floating prev / next chevron button used by the carousel
// ─────────────────────────────────────────────────────────────────────
function CarouselChevron({ direction, onClick }) {
    const isPrev = direction === "prev";
    return (
        <button
            type="button"
            onClick={onClick}
            aria-label={isPrev ? "Previous announcement" : "Next announcement"}
            className="d-flex align-items-center justify-content-center"
            style={{
                position: "absolute",
                top: "50%",
                transform: "translateY(-50%)",
                [isPrev ? "left" : "right"]: 12,
                width: 36,
                height: 36,
                borderRadius: "50%",
                background: "var(--ann-pill-bg, rgba(255,255,255,0.95))",
                border: "none",
                boxShadow: "0 2px 10px rgba(0,0,0,0.18)",
                color: "var(--ssw-text-1, #212529)",
                cursor: "pointer",
                zIndex: 5,
            }}
        >
            <i
                className={isPrev ? "ri-arrow-left-s-line" : "ri-arrow-right-s-line"}
                style={{ fontSize: 22, lineHeight: 1 }}
            />
        </button>
    );
}

// ─────────────────────────────────────────────────────────────────────
// Single card
// ─────────────────────────────────────────────────────────────────────
function AnnouncementCard({ a, canManage, onEdit, onDelete }) {
    const [hovered, setHovered] = useState(false);
    const src = announcementImageSrc(a);
    const recent = isRecent(a.createdAt);

    return (
        <div
            className="h-100 d-flex flex-column position-relative"
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            style={{
                background: "var(--ann-card-bg, #fff)",
                borderRadius: 12,
                overflow: "hidden",
                boxShadow: hovered
                    ? "0 8px 24px rgba(58, 50, 135, 0.12)"
                    : "0 2px 8px rgba(0, 0, 0, 0.04)",
                transform: hovered ? "translateY(-2px)" : "translateY(0)",
                transition: "transform 0.18s ease, box-shadow 0.18s ease",
            }}
        >
            {/* ── BANNER ─────────────────────────────────────── */}
            {/*
                Image rendering — Round 10c.
                Goal: never crop the uploaded image. A nose-only close-up
                isn't a "feature". So instead of object-fit:cover (which
                crops to fill), we use object-fit:contain (which shrinks
                to fit) and fill the empty side/letterbox area with a
                blurred copy of the same image. Same pattern that
                Spotify / YouTube / Apple Music use — handles landscape,
                portrait, and square photos all gracefully.
            */}
            <div className="position-relative" style={{ flexShrink: 0 }}>
                {src ? (
                    <div
                        style={{
                            position: "relative",
                            width: "100%",
                            height: 240,
                            overflow: "hidden",
                            background: "#1f2333",
                        }}
                    >
                        {/* Blurred backdrop — same image, scaled up and blurred
                            to hide its own edges. Slightly dimmed so the main
                            image stands out. */}
                        <img
                            src={src}
                            alt=""
                            aria-hidden="true"
                            style={{
                                position: "absolute",
                                inset: 0,
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                                filter: "blur(24px) brightness(0.85)",
                                transform: "scale(1.1)",
                            }}
                        />
                        {/* Actual image, never cropped */}
                        <img
                            src={src}
                            alt=""
                            style={{
                                position: "absolute",
                                inset: 0,
                                width: "100%",
                                height: "100%",
                                objectFit: "contain",
                                objectPosition: "center",
                                display: "block",
                            }}
                        />
                    </div>
                ) : (
                    <div
                        style={{
                            width: "100%",
                            height: 240,
                            background: gradientFor(a.id),
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "rgba(255,255,255,0.6)",
                        }}
                        aria-hidden="true"
                    >
                        <i className="ri-megaphone-line" style={{ fontSize: 56 }} />
                    </div>
                )}

                {/* NEW badge */}
                {recent && (
                    <Badge
                        color="success"
                        pill
                        className="position-absolute"
                        style={{
                            top: 12, left: 12,
                            fontSize: 10,
                            fontWeight: 600,
                            letterSpacing: 0.5,
                            padding: "4px 10px",
                            boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
                        }}
                    >
                        NEW
                    </Badge>
                )}

                {/* Edit / Delete pill */}
                {canManage && (
                    <div
                        className="position-absolute d-flex gap-1"
                        style={{
                            top: 12, right: 12,
                            background: "var(--ann-pill-bg, rgba(255,255,255,0.95))",
                            borderRadius: 999,
                            padding: "2px 4px",
                            boxShadow: "0 2px 6px rgba(0,0,0,0.12)",
                        }}
                    >
                        <Button
                            color="link"
                            size="sm"
                            className="p-1 text-secondary"
                            title="Edit"
                            onClick={onEdit}
                            style={{ lineHeight: 1 }}
                        >
                            <i className="ri-pencil-line" style={{ fontSize: 14 }}></i>
                        </Button>
                        <Button
                            color="link"
                            size="sm"
                            className="p-1 text-danger"
                            title="Delete"
                            onClick={onDelete}
                            style={{ lineHeight: 1 }}
                        >
                            <i className="ri-delete-bin-line" style={{ fontSize: 14 }}></i>
                        </Button>
                    </div>
                )}
            </div>

            {/* ── BODY ───────────────────────────────────────── */}
            <div className="p-3 flex-grow-1 d-flex flex-column">
                <h5 className="fw-semibold mb-2" style={{ color: "var(--ssw-text-1, #212529)", fontSize: 17, lineHeight: 1.3 }}>
                    {a.title}
                </h5>

                {/* Meta line: avatar + author + relative date */}
                <div className="d-flex align-items-center gap-2 mb-3 text-muted small">
                    <div
                        className="rounded-circle d-flex align-items-center justify-content-center text-white fw-bold"
                        style={{
                            width: 24, height: 24,
                            background: gradientFor(a.createdByEmpId),
                            fontSize: 10,
                            flexShrink: 0,
                        }}
                        aria-hidden="true"
                    >
                        {computeInitials(a.createdByName || a.createdByEmpId)}
                    </div>
                    <span className="fw-medium" style={{ color: "#5a6378" }}>
                        {a.createdByName || a.createdByEmpId}
                    </span>
                    <span style={{ color: "#b8bcc8" }}>·</span>
                    <span title={new Date(a.createdAt).toLocaleString()}>
                        {formatRelativeTime(a.createdAt)}
                    </span>
                </div>

                {a.body && (
                    <p
                        className="mb-0"
                        style={{
                            color: "#5a6378",
                            fontSize: 14,
                            lineHeight: 1.5,
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word",
                        }}
                    >
                        {a.body}
                    </p>
                )}
            </div>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────
// Editor modal (used for both create and edit)
// ─────────────────────────────────────────────────────────────────────
function AnnouncementEditor({ isOpen, onClose, onSaved, initial }) {
    const [title, setTitle] = useState("");
    const [body, setBody]   = useState("");
    const [imagePayload, setImagePayload] = useState(null);
    const [previewSrc, setPreviewSrc] = useState(null);
    const [clearExistingImage, setClearExistingImage] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError]   = useState(null);
    const fileRef = useRef(null);

    useEffect(() => {
        if (!isOpen) return;
        setTitle(initial?.title || "");
        setBody(initial?.body  || "");
        setImagePayload(null);
        setClearExistingImage(false);
        setPreviewSrc(initial ? announcementImageSrc(initial) : null);
        setError(null);
        if (fileRef.current) fileRef.current.value = "";
    }, [isOpen, initial]);

    const onPickFile = async (e) => {
        const f = e.target.files?.[0];
        if (!f) { setImagePayload(null); return; }
        if (f.size > MAX_IMAGE_BYTES) {
            setError("Image is over 5 MB. Please pick a smaller one.");
            e.target.value = "";
            return;
        }
        try {
            const payload = await fileToImagePayload(f);
            setImagePayload(payload);
            setClearExistingImage(false);
            if (payload) setPreviewSrc(`data:${payload.imageMimeType};base64,${payload.imageBase64}`);
            setError(null);
        } catch {
            setError("Could not read the selected image.");
        }
    };

    const onClearImage = () => {
        setImagePayload(null);
        setPreviewSrc(null);
        setClearExistingImage(true);
        if (fileRef.current) fileRef.current.value = "";
    };

    const submit = async () => {
        if (!title.trim()) { setError("Title is required."); return; }
        setSaving(true); setError(null);
        try {
            let saved;
            if (initial) {
                saved = await updateAnnouncement(initial.id, {
                    title: title.trim(),
                    body,
                    imageBase64:   imagePayload?.imageBase64   || null,
                    imageMimeType: imagePayload?.imageMimeType || null,
                    clearImage:    clearExistingImage,
                });
            } else {
                saved = await createAnnouncement({
                    title: title.trim(),
                    body,
                    imageBase64:   imagePayload?.imageBase64   || null,
                    imageMimeType: imagePayload?.imageMimeType || null,
                });
            }
            onSaved(saved);
        } catch (e) {
            const msg = e?.response?.data?.error || "Could not save. Please retry.";
            setError(msg);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal isOpen={isOpen} toggle={onClose} size="lg">
            <ModalHeader toggle={onClose}>
                {initial ? "Edit Announcement" : "New Announcement"}
            </ModalHeader>
            <ModalBody>
                <Form onSubmit={(e) => { e.preventDefault(); submit(); }}>
                    <FormGroup>
                        <Label className="fw-semibold small">Title</Label>
                        <Input
                            type="text"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            maxLength={200}
                            placeholder="Operational growth, Q1 town hall, etc."
                            autoFocus
                        />
                    </FormGroup>
                    <FormGroup>
                        <Label className="fw-semibold small">Body</Label>
                        <Input
                            type="textarea"
                            rows={5}
                            value={body}
                            onChange={(e) => setBody(e.target.value)}
                            placeholder="What's the message?"
                        />
                    </FormGroup>
                    <FormGroup>
                        <Label className="fw-semibold small d-block">Image (optional, up to 5 MB)</Label>
                        {previewSrc && (
                            <div className="mb-2">
                                <img
                                    src={previewSrc}
                                    alt="preview"
                                    style={{ maxWidth: "100%", maxHeight: 240, borderRadius: 8, border: "1px solid #eef0f4" }}
                                />
                                <div>
                                    <Button color="link" size="sm" className="text-danger p-0" onClick={onClearImage}>
                                        <i className="ri-delete-bin-line align-bottom me-1"></i>Remove image
                                    </Button>
                                </div>
                            </div>
                        )}
                        <Input
                            type="file"
                            innerRef={fileRef}
                            accept="image/*"
                            onChange={onPickFile}
                        />
                        <div className="text-muted small mt-1">
                            Tip: landscape banners (1200×600 or wider) look best.
                        </div>
                    </FormGroup>
                    {error && <div className="text-danger small mb-2">{error}</div>}
                </Form>
            </ModalBody>
            <ModalFooter>
                <Button color="light" onClick={onClose} disabled={saving}>Cancel</Button>
                <Button color="primary" onClick={submit} disabled={saving || !title.trim()}>
                    {saving ? <><Spinner size="sm" /> Saving…</> : (initial ? "Save changes" : "Post")}
                </Button>
            </ModalFooter>
        </Modal>
    );
}
